/*
 * notify-me-modal.js — `<notify-me-modal-component>` driver.
 *
 * Listens for the `pdp:open-notify` CustomEvent dispatched by
 * the Purchase Form's ATC state machine (assets/product-
 * purchase-form.js) when the resolved state is 'notify'
 * (out-of-stock variant, no backorders, notify modal block
 * present). Event detail carries the current variant.
 *
 * Form submission targets Klaviyo's Back-in-Stock API:
 *
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/
 *        ?company_id={companyId}
 *
 *   { data: { type: 'back-in-stock-subscription',
 *             attributes: { profile: { ... }, channels: ['EMAIL'] },
 *             relationships: { variant: { ... } } } }
 *
 * When the merchant enables newsletter signup AND the shopper
 * checks the newsletter checkbox AND a Klaviyo list id is
 * configured, the modal fires a second POST to the
 * subscriptions endpoint to add the email to that list.
 *
 * Klaviyo company + list IDs come from the host's data
 * attributes (set server-side via the klaviyo-company-id /
 * klaviyo-list-id snippets with type: 'bis'). Close handlers
 * cover overlay click, X button, Escape, and the Continue
 * Shopping button in the success state.
 *
 * Ported from v3.0's assets/notify-me-modal.js +
 * src/js/includes/klaviyo.js, minus the jQuery dependency.
 */

const COMPONENT = 'notify-me-modal-component';
const BIS_API_REVISION = '2025-01-15';
const SUBSCRIPTIONS_API_REVISION = '2024-06-15';

class NotifyMeModalComponent extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.form = this.querySelector('[data-notify-form]');
    this.submitButton = this.querySelector('.notify-me-modal__submit');
    this.submitLabel = this.querySelector('.notify-me-modal__submit-label');
    this.submitSpinner = this.querySelector('.notify-me-modal__submit-spinner');
    this.successEl = this.querySelector('.notify-me-modal__success');
    this.errorEl = this.querySelector('.notify-me-modal__error');
    this.variantIdInput = this.querySelector('[data-notify-variant-id]');
    this.variantTitleEl = this.querySelector('[data-modal-variant-title]');
    this.variantPriceEl = this.querySelector('[data-modal-price]');
  }

  connectedCallback() {
    this.companyId = this.dataset.companyId || '';
    this.listId = this.dataset.listId || '';

    this._bindClose();
    this._bindOpenEvent();
    this._bindEscape();

    if (this.form) {
      this.form.addEventListener(
        'submit',
        (e) => this._handleSubmit(e),
        { signal: this.abortController.signal }
      );
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  _bindClose() {
    this.querySelectorAll('[data-notify-close]').forEach((el) => {
      el.addEventListener('click', () => this._close(), {
        signal: this.abortController.signal,
      });
    });
  }

  _bindOpenEvent() {
    document.addEventListener(
      'pdp:open-notify',
      (event) => this._open(event.detail || {}),
      { signal: this.abortController.signal }
    );
  }

  _bindEscape() {
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && document.body.classList.contains('notify-me-modal--show')) {
          this._close();
        }
      },
      { signal: this.abortController.signal }
    );
  }

  _open(detail) {
    const variant = detail.variant || null;
    this._populateVariantContext(variant);
    if (variant && this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }
    document.body.classList.add('notify-me-modal--show');
    requestAnimationFrame(() => {
      const firstInput = this.form?.querySelector('input:not([type="hidden"]):not([type="checkbox"])');
      if (firstInput) firstInput.focus();
    });
  }

  _close() {
    document.body.classList.remove('notify-me-modal--show');
    setTimeout(() => {
      if (this.form) {
        this.form.hidden = false;
        this.form.reset();
      }
      if (this.successEl) this.successEl.hidden = true;
      this._hideError();
    }, 300);
  }

  _populateVariantContext(variant) {
    this.querySelectorAll('[data-variant-image]').forEach((el) => {
      el.style.display = 'none';
    });
    const variantId = variant?.id != null ? String(variant.id) : '';
    let matched = null;
    if (variantId) {
      matched = this.querySelector(`[data-variant-image="${variantId}"]`);
    }
    const fallback = this.querySelector('[data-variant-image="default"]');
    (matched || fallback)?.style.removeProperty('display');

    if (this.variantTitleEl) {
      const title = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
      this.variantTitleEl.textContent = title;
      this.variantTitleEl.hidden = !title;
    }

    if (this.variantPriceEl && variant?.price != null) {
      this.variantPriceEl.innerHTML = this._formatMoney(variant.price);
    }
  }

  _formatMoney(cents) {
    const currency =
      document.getElementById('currency-symbol')?.value || '$';
    const value = Math.floor(Number(cents));
    const whole = Math.floor(value / 100);
    const fraction = String(value % 100).padStart(2, '0');
    return `${currency}${whole}.${fraction}`;
  }

  async _handleSubmit(event) {
    event.preventDefault();
    if (!this.form) return;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    if (!this.companyId) {
      console.error('[NotifyMeModal] Klaviyo company_id missing — configure Theme Settings → Klaviyo (Back-in-Stock).');
      this._showError();
      return;
    }

    const formData = new FormData(this.form);
    const payload = Object.fromEntries(formData.entries());

    const variantId = payload.variant_id;
    const email = payload.email;
    const firstName = payload.first_name;
    const lastName = payload.last_name;
    const newsletterRequested = payload.signup_mailing_list === 'on';

    if (!variantId || !email) {
      this._showError();
      return;
    }

    this._setLoading(true);
    this._hideError();

    try {
      await this._submitBackInStock(variantId, email);

      // Optional newsletter signup runs after BIS succeeds — a
      // failure here doesn't block the success state since the
      // primary intent (BIS subscription) already worked.
      if (newsletterRequested && this.listId) {
        try {
          await this._submitNewsletter(email, firstName, lastName);
        } catch (newsletterErr) {
          console.warn('[NotifyMeModal] Newsletter signup failed:', newsletterErr);
        }
      }

      this._showSuccess();
    } catch (err) {
      console.error('[NotifyMeModal] Back-in-stock subscription failed:', err);
      this._showError();
    } finally {
      this._setLoading(false);
    }
  }

  /**
   * Klaviyo Back-in-Stock API call. The variant id needs the
   * `$shopify:::$default:::` prefix per Klaviyo's catalog-variant
   * id format. 202 indicates accepted.
   */
  async _submitBackInStock(variantId, email) {
    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`;
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        revision: BIS_API_REVISION,
        'content-type': 'application/vnd.api+json',
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.errors?.[0]?.detail || `BIS subscription failed (HTTP ${response.status})`);
    }
  }

  /**
   * Optional companion Klaviyo Subscriptions API call when the
   * shopper opts into the newsletter list. Uses the list id
   * from the host's data attribute (configured in Theme Settings
   * → Klaviyo Back-in-Stock).
   */
  async _submitNewsletter(email, firstName, lastName) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`;
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Product Back-in-Stock Modal',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                ...(firstName ? { first_name: firstName } : {}),
                ...(lastName ? { last_name: lastName } : {}),
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        revision: SUBSCRIPTIONS_API_REVISION,
        'content-type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.errors?.[0]?.detail || `Newsletter subscription failed (HTTP ${response.status})`);
    }
  }

  _setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', String(isLoading));
    if (this.submitLabel) this.submitLabel.hidden = isLoading;
    if (this.submitSpinner) this.submitSpinner.hidden = !isLoading;
  }

  _showSuccess() {
    if (this.form) this.form.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
  }

  _showError() {
    if (this.errorEl) this.errorEl.hidden = false;
  }

  _hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

if (!customElements.get(COMPONENT)) {
  customElements.define(COMPONENT, NotifyMeModalComponent);
}
